/**
 * Engine Editor MAP enforcement sub-region — (transformation × decision) cell
 * per scope-doc §2 cell-by-cell routing. Day 3 P1+P2+P3 integrated implementation.
 *
 * Extracted from inline MapEnforcementSubRegion at EngineEditorDecisionRulesCluster.tsx
 * per Day 1 P4.3 anticipation ("Future Day 2/3 may extract specific sub-regions
 * to separate files when positive 4a affordances + composition rendering grow
 * beyond inline scope") — Day 3 brings substantial substrate fetch + brand
 * selector + panel orchestration + gate-refusal-with-pathway that warrants
 * extraction.
 *
 * P1: aggregate position view + brand-scoped edit entry
 *   - Aggregate display per Atlas MapEnforcementCard.tsx pattern verbatim:
 *     "ISG enforces MAP on N of M brands. Flag-and-report posture — engine
 *     surfaces violations to buyers rather than blocking sales." (or N=0 variant)
 *   - Brand selector with client-side filter (911 active brands per Day 3 P0
 *     substrate verification — small enough for single fetch + local search;
 *     ProductPickerTypeahead's server-side ILIKE pattern is overkill here)
 *
 * P2: brands.enforces_map column-surgical edit via EngineEditorBrandsEnforcesMapPanel
 *   - Reuses slide-in shell layout from Day 2.5 ownership-cascading panel
 *     (duplicated structure pending Day 4 P2 shared-shell extraction consideration)
 *   - Ownership-cascading composition per scope §5.5.1 amended (6 components)
 *
 * P3: gate-refusal-with-pathway per scope §6.2
 *   - detectMapEnforcementGate pure function determines refuse/proceed
 *   - Flip-to-true gated by products.map_price availability on the brand
 *   - Flip-to-false never gated
 *   - Per §6.2: "no edit form opens — the deflection IS the affordance"
 *   - COUNT query fires at edit-attempt (P3.a design — bounded to the moment
 *     of the flip, not every paint or region render)
 *   - Refusal renders inline + cross-link deflection to Files → Algopix Import
 *
 * Anchor id `#map-enforcement` per scope-doc §2 line 263 — Atlas §6.6.1 MAP
 * enforcement cross-link migrates from Directory → Brands to this anchor at
 * Day 3 P4.
 */

import { useEffect, useState, useMemo, type JSX } from 'react';
import { ShieldCheck, AlertTriangle } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import {
  detectMapEnforcementGate,
  type MapEnforcementGateDecision,
} from '../../../lib/engine-editor/f33Gating';
import { URL_FILES_ALGOPIX_IMPORT } from '../../../lib/engine-editor/engineEditorUrls';
import EngineEditorBrandsEnforcesMapPanel, {
  type BrandRowState,
} from '../affordances/EngineEditorBrandsEnforcesMapPanel';

const MAX_VISIBLE_BRANDS = 20;

export default function EngineEditorMapEnforcementSubRegion(): JSX.Element {
  const [brands, setBrands] = useState<BrandRowState[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [fetchError, setFetchError] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [activeBrand, setActiveBrand] = useState<BrandRowState | null>(null);
  const [gateDecision, setGateDecision] = useState<MapEnforcementGateDecision | null>(null);
  const [gateChecking, setGateChecking] = useState<boolean>(false);
  const [gateError, setGateError] = useState<string | null>(null);

  const fetchBrands = (): (() => void) => {
    let cancelled = false;
    setLoading(true);
    // Single fetch of all active brands + enforces_map state per P0 substrate
    // verification (911 brands; small enough for one round-trip + local filter).
    supabase
      .from('brands')
      .select('id, name, enforces_map')
      .eq('is_active', true)
      .order('name', { ascending: true })
      .then((res) => {
        if (cancelled) return;
        if (res.error) {
          setFetchError(true);
          setLoading(false);
          return;
        }
        setBrands((res.data ?? []) as unknown as BrandRowState[]);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  };

  useEffect(() => {
    return fetchBrands();
  }, []);

  // Aggregate state — N enforcing of M active brands per Atlas MapEnforcementCard pattern.
  const totalActiveBrands = brands.length;
  const enforcesMapCount = brands.filter((b) => b.enforces_map).length;

  // Client-side filter over fetched brands (911 rows × small payload — local
  // filter feels more responsive than server-side ILIKE + 300ms debounce).
  const filteredBrands = useMemo<BrandRowState[]>(() => {
    const q = search.trim().toLowerCase();
    if (!q) return brands.slice(0, MAX_VISIBLE_BRANDS);
    return brands
      .filter((b) => b.name.toLowerCase().includes(q))
      .slice(0, MAX_VISIBLE_BRANDS);
  }, [brands, search]);

  // Aggregate surface text per Atlas MapEnforcementCard.tsx pattern verbatim.
  const aggregateText =
    enforcesMapCount > 0
      ? `ISG enforces MAP on ${enforcesMapCount} of ${totalActiveBrands} brands. Flag-and-report posture — engine surfaces violations to buyers rather than blocking sales.`
      : `ISG has not yet configured MAP enforcement on any of ${totalActiveBrands} active brands. Substrate ready — flag-and-report posture activates when brands.enforces_map populates.`;

  const handleBrandSelect = async (brand: BrandRowState): Promise<void> => {
    // Determine direction: if currently enforcing, admin can only flip-to-false
    // (never gated). If not currently enforcing, admin wants flip-to-true (gate-
    // refusal-with-pathway applies per scope §6.2). For flip-to-false, open
    // panel directly. For flip-to-true intent, fire gate check.
    if (brand.enforces_map === true) {
      // Admin can only flip-to-false from current=true; never gated.
      setActiveBrand(brand);
      setGateDecision('proceed');
      return;
    }
    // Flip-to-true intent: fire COUNT query per P3.a (at edit-attempt, not on
    // region render or selection). Round trip preserves write-authority
    // distinction per §6.2.
    setActiveBrand(brand);
    setGateChecking(true);
    setGateError(null);
    const { count, error: countError } = await supabase
      .from('products')
      .select('id', { count: 'exact', head: true })
      .eq('brand_id', brand.id)
      .not('map_price', 'is', null);
    setGateChecking(false);
    if (countError) {
      setGateError(countError.message);
      return;
    }
    const decision = detectMapEnforcementGate(count ?? 0, false, true);
    setGateDecision(decision);
  };

  const handleEditClose = (): void => {
    setActiveBrand(null);
    setGateDecision(null);
    setGateError(null);
  };

  const handleEditSaved = (): void => {
    setActiveBrand(null);
    setGateDecision(null);
    setGateError(null);
    fetchBrands();
  };

  return (
    <div
      id="map-enforcement"
      className="border border-border-subtle bg-surface-2 rounded p-3 space-y-2"
    >
      <div className="flex items-start gap-2">
        <ShieldCheck size={14} className="text-ink-secondary flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="text-xs font-semibold text-ink-bright">MAP enforcement</h3>

          {/* COMPOSABILITY: per-section "Last changed X days ago" indicator slot — wires post-Day 92 */}

          {fetchError ? (
            <p className="text-xs text-ink-muted italic mt-1">
              MAP enforcement substrate currently unavailable.
            </p>
          ) : loading ? (
            <div
              className="mt-1 h-4 bg-surface-4 rounded animate-pulse"
              aria-label="Loading MAP enforcement substrate"
            />
          ) : (
            <>
              <p className="text-xs text-ink-secondary mt-1">{aggregateText}</p>
              <p className="text-xs text-ink-muted italic mt-1">
                Engine read: enforces_map=true AND map_price IS NOT NULL AND sale_price &lt;
                map_price → below_map=true.
              </p>

              {/* Brand selector — client-side filter; max-20 visible per scroll budget */}
              <div className="mt-3">
                <label
                  htmlFor="map-enforcement-brand-search"
                  className="block text-xs font-semibold text-ink-bright mb-1"
                >
                  Edit a brand’s MAP enforcement
                </label>
                <input
                  id="map-enforcement-brand-search"
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={`Search ${totalActiveBrands} brands…`}
                  disabled={activeBrand !== null}
                  className="w-full px-2 py-1 text-xs bg-surface-1 border border-border rounded text-ink-primary focus:border-accent focus:outline-none disabled:opacity-40"
                />
                <ul className="mt-1 max-h-48 overflow-y-auto border border-border-subtle bg-surface-1 rounded divide-y divide-border-subtle">
                  {filteredBrands.length === 0 ? (
                    <li className="px-2 py-1 text-xs text-ink-muted italic">
                      No matches.
                    </li>
                  ) : (
                    filteredBrands.map((brand) => (
                      <li key={brand.id}>
                        <button
                          type="button"
                          onClick={() => void handleBrandSelect(brand)}
                          disabled={activeBrand !== null}
                          className="w-full text-left px-2 py-1 text-xs text-ink-secondary hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer flex items-center justify-between gap-2"
                        >
                          <span className="truncate">{brand.name}</span>
                          <span
                            className={
                              brand.enforces_map
                                ? 'text-2xs text-amber-500 font-mono'
                                : 'text-2xs text-ink-muted font-mono'
                            }
                          >
                            {brand.enforces_map ? 'enforcing' : 'not enforcing'}
                          </span>
                        </button>
                      </li>
                    ))
                  )}
                </ul>
                {search.trim() === '' && totalActiveBrands > MAX_VISIBLE_BRANDS && (
                  <p className="text-2xs text-ink-muted italic mt-1">
                    Showing first {MAX_VISIBLE_BRANDS} of {totalActiveBrands} — search to
                    narrow.
                  </p>
                )}
              </div>
            </>
          )}

          {/* Gate check in-flight state */}
          {gateChecking && activeBrand && (
            <p className="text-xs text-ink-muted italic mt-2">
              Checking MAP availability for {activeBrand.name}…
            </p>
          )}

          {/* Gate check error state */}
          {gateError && (
            <div className="mt-2 bg-red-500/10 border border-red-500/30 rounded p-2">
              <p className="text-xs text-red-500">
                Gate check failed: {gateError}{' '}
                <button
                  type="button"
                  onClick={handleEditClose}
                  className="text-accent hover:underline cursor-pointer"
                >
                  Dismiss
                </button>
              </p>
            </div>
          )}

          {/* Gate-refusal-with-pathway per scope §6.2 — "no edit form opens —
              the deflection IS the affordance". Refusal copy verbatim per §6.2. */}
          {gateDecision === 'refuse' && activeBrand && (
            <div className="mt-2 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-amber-500 space-y-1">
                <p>
                  <strong>Cannot enable MAP enforcement for {activeBrand.name}:</strong> no
                  MAP values exist on this brand’s products. Import MAP values via Algopix
                  pipeline before enabling enforcement.
                </p>
                <p>
                  <a
                    href={URL_FILES_ALGOPIX_IMPORT}
                    className="text-accent hover:underline not-italic"
                  >
                    Files → Algopix Import →
                  </a>
                  <button
                    type="button"
                    onClick={handleEditClose}
                    className="ml-3 text-ink-muted hover:text-ink-primary cursor-pointer not-italic underline"
                  >
                    Dismiss
                  </button>
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit panel — opens when gate decision === 'proceed'. Either:
          - admin selected brand with enforces_map=true (flip-to-false; never gated), OR
          - admin selected brand with enforces_map=false AND gate check returned 'proceed'
            (count>0 products with map_price). */}
      {activeBrand && gateDecision === 'proceed' && (
        <EngineEditorBrandsEnforcesMapPanel
          brand={activeBrand}
          onSaved={handleEditSaved}
          onClose={handleEditClose}
        />
      )}
    </div>
  );
}
